import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import cx from "classnames";

import { useCombobox } from "./Combobox";

import { Flex } from "../Flex";
import { Text } from "../Text";
import { Spinner } from "../Spinner";

import styles from "./Combobox.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";
import listViewStyles from "../ListView/ListView.module.scss";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import { ComboboxLegacyList } from "../ComboboxLegacy";

/**
 * Props for the ComboboxList component
 */
export type ComboboxListProps = ComponentPropsWithoutRef<"ul" | "div"> & {
  children?: ReactNode;
};

const ComboboxListElement = function <Item = any>({
  children,
  ...rest
}: ComboboxListProps) {
  const {
    getMenuProps,
    multiple,
    items,
    hasAddNew = false,
    hasEmpty,
    loading,
  } = useCombobox<Item>();

  if (!supportsPopover())
    return <ComboboxLegacyList {...rest}>{children}</ComboboxLegacyList>;

  if (multiple) {
    return (
      <>
        <div
          {...getMenuProps(rest as ComponentPropsWithoutRef<"div">)}
          data-anv="combobox-list"
          className={cx(styles["listview"], listViewStyles["listview"])}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
            overflow: "auto",
          }}
        >
          {children}
        </div>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner className={styles["loading-spinner"]} />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  } else {
    return (
      <>
        <ul
          {...getMenuProps(rest as ComponentPropsWithoutRef<"ul">)}
          data-anv="combobox-list"
          className={listboxStyles["listbox"]}
          style={{
            display: items.length || hasAddNew ? undefined : "none",
            overflow: "auto",
          }}
        >
          {children}
        </ul>

        {loading ? (
          <div className={styles["empty"]}>
            <Flex justifyContent="center">
              <Spinner className={styles["loading-spinner"]} />
            </Flex>
          </div>
        ) : !items.length && !hasEmpty ? (
          <div className={styles["empty"]}>
            <Text subdued size="small">
              No match found
            </Text>
          </div>
        ) : null}
      </>
    );
  }
};

/**
 * ComboboxList component for rendering the list container of combobox items
 *
 * Features:
 * - Supports both single and multiple selection modes
 * - Automatically handles empty state with "No match found" message
 * - Shows loading spinner during async operations
 * - Integrates with combobox state management
 * - Supports popover and legacy fallback modes
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered combobox list container
 *
 * @example
 * <ComboboxList>
 *   {items.map((item, index) => (
 *     <ComboboxItem key={item.id} item={item} index={index}>
 *       {item.name}
 *     </ComboboxItem>
 *   ))}
 * </ComboboxList>
 */
export const ComboboxList = function <Item = any>({
  children,
  className,
  ...rest
}: ComboboxListProps) {
  if (!supportsPopover())
    return (
      <ComboboxLegacyList<Item> className={className} {...rest}>
        {children}
      </ComboboxLegacyList>
    );

  return (
    <ComboboxListElement<Item> className={className} {...rest}>
      {children}
    </ComboboxListElement>
  );
};
